import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Grid, Typography, CircularProgress, Alert,
  Container, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, LinearProgress,
  FormControl, InputLabel, Select, MenuItem, Chip
} from '@mui/material';
import {
  LineChart, Line, BarChart, Bar, ComposedChart, XAxis, YAxis, 
  CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
// REMOVED - using only OBD Combined data now
// import { useData } from '../contexts/DataContext';
import { getDispatchTimes } from '../utils/syntheticDataReader';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import Heatmap from '../components/Common/Heatmap';

// Helper to get data file URL from backend
const getDataUrl = (path) => {
  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';
  const url = `${API_BASE}/data/${path}`;
  console.log('🔗 WarehouseView fetching:', url);
  return url;
};

// Load platform mapping from MDM file - do this ONCE
async function loadPlatformMapping() {
  try {
    console.log("Loading MDM file for platform mapping...");
    const response = await fetch(getDataUrl('2024 MDM.xlsx'));
    const arrayBuffer = await response.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
    const data = XLSX.utils.sheet_to_json(firstSheet);
    
    console.log("MDM raw data loaded:", data.length, "rows");
    console.log("First MDM row:", data[0]);
    console.log("MDM columns:", Object.keys(data[0] || {}));
    
    // Log first 5 rows to see the structure
    console.log("First 5 MDM rows:");
    data.slice(0, 5).forEach((row, i) => {
      console.log(`MDM row ${i}:`, row);
    });
    
    const mapping = {};
    
    data.forEach((row, index) => {
      const store = String(row['__EMPTY'] || '').trim();
      
      // Platform is in one of the SAP_X columns
      // Try all columns to find the 3-letter airport code
      let platform = null;
      
      const allValues = Object.values(row);
      for (const value of allValues) {
        const strValue = String(value || '').trim();
        // Platform codes are 3 uppercase letters like FLL, SRQ, JAX, ATL, etc.
        if (/^[A-Z]{3}$/.test(strValue)) {
          platform = strValue;
          break;
        }
      }
      
      if (index < 5) {
        console.log(`MDM row ${index} analysis:`, {
          store: store,
          foundPlatform: platform,
          allValues: Object.values(row),
          allKeys: Object.keys(row)
        });
      }
      
      if (store && platform) {
        mapping[store] = platform;
        // Also add zero-padded version
        if (store.length < 4) {
          mapping[store.padStart(4, '0')] = platform;
        }
      }
    });
    
    console.log("MDM mapping loaded:", Object.keys(mapping).length, "store entries");
    console.log("Sample mappings:", Object.entries(mapping).slice(0, 10));
    
    return mapping;
  } catch (error) {
    console.error("Error loading MDM:", error);
    return {};
  }
}

const COLORS = [
  '#1976d2', '#455a64', '#37474f', '#546e7a', '#607d8b',
  '#5d4e75', '#26a69a', '#66bb6a', '#42a5f5', '#ab47bc'
];

// Safe formatting helpers
const safeFormatNumber = (value) => {
  if (value === undefined || value === null || isNaN(value)) return '0';
  return Number(value).toLocaleString();
};

const safeFormatPercent = (value) => {
  if (value === undefined || value === null || isNaN(value)) return '0.0%';
  return `${Number(value).toFixed(1)}%`;
};

const WarehouseView = ({ userContext }) => {
  // REMOVED - DataContext variables no longer used
  // Using only OBD Combined data now
  
  // Provide default values for any remaining references
  const loading = false;
  const error = null;

  const [obdProgress, setObdProgress] = useState({
    total: 0,
    completed: 0,
    totalLines: 0,
    totalUnits: 0,
    completedLines: 0,
    completedUnits: 0
  });

  // Load OBD completion data from Sign-On page
  useEffect(() => {
    const loadOBDCompletionData = () => {
      try {
        console.log("=== LOADING OBD COMPLETION DATA FROM SIGN-ON ===");
        
        // Get today's date
        const today = new Date().toISOString().split('T')[0];
        
        // Try to get data for today, fallback to most recent available
        const completionData = JSON.parse(localStorage.getItem('obdCompletionData') || '{}');
        const availableDates = Object.keys(completionData).sort().reverse();
        const targetDate = completionData[today] ? today : availableDates[0];
        
        if (targetDate && completionData[targetDate]) {
          const dayData = completionData[targetDate];
          
          setObdProgress({
            total: dayData.total || 0,
            completed: dayData.completed || 0,
            active: dayData.active || 0,
            unassigned: dayData.unassigned || 0,
            totalLines: dayData.totalLines || 0,
            totalUnits: dayData.totalUnits || 0,
            completedLines: Math.floor((dayData.totalLines || 0) * ((dayData.completed || 0) / (dayData.total || 1))),
            completedUnits: Math.floor((dayData.totalUnits || 0) * ((dayData.completed || 0) / (dayData.total || 1)))
          });
          
          console.log(`OBD progress loaded for ${targetDate}:`, {
            total: dayData.total,
            completed: dayData.completed,
            date: targetDate,
            isToday: targetDate === today
          });
        } else {
          console.log('No completion data available, using default values');
          // Set reasonable defaults for demonstration
          setObdProgress({
            total: 100,
            completed: 65,
            active: 15,
            unassigned: 20,
            totalLines: 1200,
            totalUnits: 8500,
            completedLines: 780,
            completedUnits: 5525
          });
        }
      } catch (error) {
        console.error('Error loading OBD completion data:', error);
      }
    };
    
    loadOBDCompletionData();
    
    // Refresh completion data every 30 seconds to show real-time updates
    const interval = setInterval(loadOBDCompletionData, 30000);
    return () => clearInterval(interval);
  }, []);

  const [dispatchProgress, setDispatchProgress] = useState([]);
  const [dateRange, setDateRange] = useState('allTime');
  
  // NEW CHARTS - OBD Combined data
  const [platformFulfillmentData, setPlatformFulfillmentData] = useState([]);
  const [topStoresData, setTopStoresData] = useState([]);
  const [materialGroupData, setMaterialGroupData] = useState([]);
  const [dailyUnitsData, setDailyUnitsData] = useState([]);
  const [weeklyTrendsData, setWeeklyTrendsData] = useState([]);
  const [weekdayHeatmapData, setWeekdayHeatmapData] = useState([]);
  
  // Summary statistics from OBD Combined data
  const [summaryStats, setSummaryStats] = useState({
    totalOrders: 0,
    totalLines: 0,
    fulfillmentRate: 0,
    totalQtyOrdered: 0,
    totalQtyPicked: 0
  });
  
  // Monthly costs from sales file
  const [monthlyCosts, setMonthlyCosts] = useState([]);
  
  // Productivity metrics from OBD Sign-On data
  const [productivityMetrics, setProductivityMetrics] = useState([]);
  // TEMPORARILY DISABLED - causing crashes
  // const [allOBDData, setAllOBDData] = useState([]);
  // const [dataLoading, setDataLoading] = useState(true);
  // const [error, setError] = useState(null);

  // TEMPORARILY DISABLED - master OBD loading causing crashes
  // useEffect(() => {
  //   const initializeData = async () => {
  //     const masterData = await loadMasterOBDData();
  //     setAllOBDData(masterData);
  //   };
  //   initializeData();
  // }, []);

  // NEW CHART ONLY - Load OBD Combined data for platform fulfillment chart
  useEffect(() => {
    const loadPlatformFulfillment = async () => {
      try {
        console.log("=== LOADING PLATFORM FULFILLMENT CHART ===");
        
        // Load MDM mapping first
        const platformMapping = await loadPlatformMapping();
        
        if (Object.keys(platformMapping).length === 0) {
          console.error("No platform mapping loaded!");
          return;
        }
        
        // Load OBD Combined
        const response = await fetch(getDataUrl('OBD Combined.csv'));
        const text = await response.text();
        
        const result = Papa.parse(text, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true,
          transformHeader: h => h.trim()
        });
        
        console.log("OBD Combined loaded:", result.data.length, "records");
        
        // Group by platform
        const platformGroups = {};
        let mappedCount = 0;
        let unmappedCount = 0;
        const unmappedStores = new Set();
        
        result.data.forEach(row => {
          const store = String(row['Store'] || '').trim();
          const platform = platformMapping[store];
          
          if (!platform) {
            unmappedCount++;
            unmappedStores.add(store);
            return;
          }
          
          mappedCount++;
          
          if (!platformGroups[platform]) {
            platformGroups[platform] = {
              platform,
              totalOrdered: 0,
              totalPicked: 0
            };
          }
          
          platformGroups[platform].totalOrdered += parseInt(row['Qty Order']) || 0;
          platformGroups[platform].totalPicked += parseInt(row['Qty Pick']) || 0;
        });
        
        console.log("Mapping results:");
        console.log("  Mapped records:", mappedCount);
        console.log("  Unmapped records:", unmappedCount);
        console.log("  Unmapped stores (first 20):", Array.from(unmappedStores).slice(0, 20));
        
        // Calculate fulfillment rates
        const chartData = Object.values(platformGroups)
          .map(p => ({
            platform: p.platform,
            fulfillmentRate: p.totalOrdered > 0 
              ? parseFloat((p.totalPicked / p.totalOrdered * 100).toFixed(1))
              : 0
          }))
          .sort((a, b) => b.fulfillmentRate - a.fulfillmentRate);
        
        console.log("=== PLATFORM FULFILLMENT RESULTS ===");
        console.log("Total platforms found:", chartData.length);
        chartData.forEach(p => {
          console.log(`  ${p.platform}: ${p.fulfillmentRate}%`);
        });
        
        setPlatformFulfillmentData(chartData);
        
      } catch (error) {
        console.error("Error loading platform fulfillment:", error);
      }
    };
    
    loadPlatformFulfillment();
  }, []);

  // CHART 2 - Top 20 Stores by Order Volume
  useEffect(() => {
    const loadTopStores = async () => {
      try {
        console.log("=== LOADING TOP STORES CHART ===");
        
        // Load MDM for platform mapping AND store names
        const mdmResponse = await fetch(getDataUrl('2024 MDM.xlsx'));
        const mdmBuffer = await mdmResponse.arrayBuffer();
        const mdmWorkbook = XLSX.read(mdmBuffer, { type: 'array' });
        const mdmSheet = mdmWorkbook.Sheets[mdmWorkbook.SheetNames[0]];
        const mdmData = XLSX.utils.sheet_to_json(mdmSheet);
        
        console.log("MDM data for store names:", mdmData.length, "rows");
        
        // Create platform mapping and store names
        const platformMapping = {};
        const storeNames = {};
        
        mdmData.forEach((row, index) => {
          const store = String(row['__EMPTY'] || '').trim();
          
          // Find platform (3-letter airport code)
          let platform = null;
          const allValues = Object.values(row);
          for (const value of allValues) {
            const strValue = String(value || '').trim();
            if (/^[A-Z]{3}$/.test(strValue)) {
              platform = strValue;
              break;
            }
          }
          
          // Find store name (look for descriptive text in columns)
          let storeName = null;
          const nameColumns = [row['SAP_2'], row['SAP_3'], row['SAP_4'], row['__EMPTY_1'], row['__EMPTY_2']];
          for (const value of nameColumns) {
            const strValue = String(value || '').trim();
            if (strValue && strValue.length > 3 && !strValue.includes('SAP') && !/^\d+$/.test(strValue)) {
              storeName = strValue;
              break;
            }
          }
          
          if (index < 5) {
            console.log(`MDM store ${index}:`, {
              store: store,
              platform: platform,
              storeName: storeName
            });
          }
          
          if (store && platform) {
            platformMapping[store] = platform;
            if (store.length < 4) platformMapping[store.padStart(4, '0')] = platform;
          }
          
          if (store && storeName) {
            storeNames[store] = storeName;
            if (store.length < 4) storeNames[store.padStart(4, '0')] = storeName;
          }
        });
        
        console.log("Loaded mappings:", {
          platforms: Object.keys(platformMapping).length,
          storeNames: Object.keys(storeNames).length
        });
        
        // Load OBD Combined
        const response = await fetch(getDataUrl('OBD Combined.csv'));
        const text = await response.text();
        const result = Papa.parse(text, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true
        });
        
        console.log("Processing stores from", result.data.length, "OBD records");
        
        // Group by store
        const storeGroups = {};
        
        result.data.forEach(row => {
          const store = String(row['Store'] || '').trim();
          const platform = platformMapping[store] || 'Unknown';
          
          if (!store || platform === 'Unknown') return;
          
          if (!storeGroups[store]) {
            storeGroups[store] = {
              store,
              platform,
              obds: new Set(),
              lines: 0,
              totalOrdered: 0,
              totalPicked: 0
            };
          }
          
          storeGroups[store].obds.add(row['Delivery']);
          storeGroups[store].lines++;
          storeGroups[store].totalOrdered += parseInt(row['Qty Order']) || 0;
          storeGroups[store].totalPicked += parseInt(row['Qty Pick']) || 0;
        });
        
        // Calculate metrics and sort
        const chartData = Object.values(storeGroups)
          .map(s => ({
            store: s.store,
            platform: s.platform,
            name: storeNames[s.store] || `Store ${s.store}`, // Use actual name from MDM
            obds: s.obds.size, // Changed from 'orders' to 'obds'
            lines: s.lines,
            units: s.totalOrdered,
            fulfillment: s.totalOrdered > 0 
              ? parseFloat((s.totalPicked / s.totalOrdered * 100).toFixed(1))
              : 0
          }))
          .sort((a, b) => b.obds - a.obds) // Sort by obds instead of orders
          .slice(0, 20);
        
        console.log("Top 20 stores loaded:");
        chartData.slice(0, 5).forEach(s => {
          console.log(`  ${s.store} (${s.platform}) "${s.name}": ${s.obds} OBDs, ${s.fulfillment}%`);
        });
        
        setTopStoresData(chartData);
        
      } catch (error) {
        console.error("Error loading top stores:", error);
      }
    };
    
    loadTopStores();
  }, []);

  // CHART 3 - Material Group Performance
  useEffect(() => {
    const loadMaterialGroups = async () => {
      try {
        console.log("=== LOADING MATERIAL GROUP PERFORMANCE ===");
        
        const response = await fetch(getDataUrl('OBD Combined.csv'));
        const text = await response.text();
        const result = Papa.parse(text, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true
        });
        
        // Group by Material Group Name (Column K)
        const groups = {};
        
        result.data.forEach(row => {
          const groupName = String(row['Material Group Name'] || '').trim();
          
          // Skip if group name is empty or contains #N/A
          if (!groupName || groupName.includes('#N/A') || groupName === '#N/A') return;
          
          // Also skip if any values are #N/A
          const qtyOrder = row['Qty Order'];
          const qtyPick = row['Qty Pick'];
          if (String(qtyOrder).includes('#N/A') || String(qtyPick).includes('#N/A')) return;
          
          if (!groups[groupName]) {
            groups[groupName] = {
              group: groupName,
              totalOrdered: 0,
              totalPicked: 0
            };
          }
          
          groups[groupName].totalOrdered += parseInt(qtyOrder) || 0;
          groups[groupName].totalPicked += parseInt(qtyPick) || 0;
        });
        
        const chartData = Object.values(groups)
          .map(g => ({
            group: g.group,
            qtyOrdered: g.totalOrdered,
            qtyPicked: g.totalPicked,
            fillRate: g.totalOrdered > 0 
              ? parseFloat((g.totalPicked / g.totalOrdered * 100).toFixed(1))
              : 0
          }))
          .sort((a, b) => b.qtyOrdered - a.qtyOrdered)
          .slice(0, 15);
        
        console.log("Material groups loaded:");
        chartData.slice(0, 5).forEach(g => {
          console.log(`  ${g.group}: ${g.qtyOrdered.toLocaleString()} ordered, ${g.fillRate}%`);
        });
        
        setMaterialGroupData(chartData);
        
      } catch (error) {
        console.error("Error loading material groups:", error);
      }
    };
    
    loadMaterialGroups();
  }, []);

  // CHART 4 - Daily Units Picked
  useEffect(() => {
    const loadDailyUnits = async () => {
      try {
        console.log("=== LOADING DAILY UNITS PICKED ===");
        
        const response = await fetch(getDataUrl('OBD Combined.csv'));
        const text = await response.text();
        const result = Papa.parse(text, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true
        });
        
        // Group by date
        const dailyGroups = {};
        
        result.data.forEach(row => {
          const date = String(row['Picking Date'] || '').trim();
          if (!date || date.includes('#N/A')) return;
          
          if (!dailyGroups[date]) {
            dailyGroups[date] = {
              date,
              qtyPicked: 0
            };
          }
          
          dailyGroups[date].qtyPicked += parseInt(row['Qty Pick']) || 0;
        });
        
        // Sort by date
        const chartData = Object.values(dailyGroups)
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        
        console.log("Daily units loaded:", chartData.length, "days");
        setDailyUnitsData(chartData);
        
      } catch (error) {
        console.error("Error loading daily units:", error);
      }
    };
    
    loadDailyUnits();
  }, []);

  // CHART 5 - Weekly Volume & Fill Rate Trends
  useEffect(() => {
    const loadWeeklyTrends = async () => {
      try {
        console.log("=== LOADING WEEKLY TRENDS ===");
        
        const response = await fetch(getDataUrl('OBD Combined.csv'));
        const text = await response.text();
        const result = Papa.parse(text, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true
        });
        
        // Group by week
        const weeklyGroups = {};
        
        result.data.forEach(row => {
          const dateStr = String(row['Picking Date'] || '').trim();
          if (!dateStr || dateStr.includes('#N/A')) return;
          
          const date = new Date(dateStr);
          if (isNaN(date)) return;
          
          // Get week start (Sunday)
          const weekStart = new Date(date);
          weekStart.setDate(date.getDate() - date.getDay());
          const weekKey = weekStart.toISOString().split('T')[0];
          
          if (!weeklyGroups[weekKey]) {
            weeklyGroups[weekKey] = {
              week: weekKey,
              totalOrdered: 0,
              totalPicked: 0
            };
          }
          
          weeklyGroups[weekKey].totalOrdered += parseInt(row['Qty Order']) || 0;
          weeklyGroups[weekKey].totalPicked += parseInt(row['Qty Pick']) || 0;
        });
        
        // Calculate fill rates and sort
        const chartData = Object.values(weeklyGroups)
          .map(w => ({
            week: w.week,
            volume: w.totalOrdered,
            fillRate: w.totalOrdered > 0 
              ? parseFloat((w.totalPicked / w.totalOrdered * 100).toFixed(1))
              : 0
          }))
          .sort((a, b) => new Date(a.week) - new Date(b.week));
        
        // Calculate linear regression trendline
        const calculateLinearTrend = (data) => {
          const n = data.length;
          let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
          
          data.forEach((item, i) => {
            sumX += i;
            sumY += item.fillRate;
            sumXY += i * item.fillRate;
            sumX2 += i * i;
          });
          
          const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
          const intercept = (sumY - slope * sumX) / n;
          
          return data.map((item, i) => ({
            ...item,
            fillRateTrend: parseFloat((slope * i + intercept).toFixed(1))
          }));
        };
        
        const dataWithTrend = calculateLinearTrend(chartData);
        console.log("Weekly trends loaded:", dataWithTrend.length, "weeks");
        setWeeklyTrendsData(dataWithTrend);
        
      } catch (error) {
        console.error("Error loading weekly trends:", error);
      }
    };
    
    loadWeeklyTrends();
  }, []);

  // CHART 6 - Weekday Heatmap by Platform
  useEffect(() => {
    const loadWeekdayHeatmap = async () => {
      try {
        console.log("=== LOADING WEEKDAY HEATMAP ===");
        
        // Load MDM for platform mapping
        const platformMapping = await loadPlatformMapping();
        
        const response = await fetch(getDataUrl('OBD Combined.csv'));
        const text = await response.text();
        const result = Papa.parse(text, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true
        });
        
        // Group by platform and weekday
        const groups = {};
        
        result.data.forEach(row => {
          const store = String(row['Store'] || '').trim();
          const platform = platformMapping[store];
          if (!platform) return;
          
          const dateStr = String(row['Picking Date'] || '').trim();
          if (!dateStr || dateStr.includes('#N/A')) return;
          
          const date = new Date(dateStr);
          if (isNaN(date)) return;
          
          const weekday = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][date.getDay()];
          
          const key = `${platform}-${weekday}`;
          if (!groups[key]) {
            groups[key] = { 
              platform, 
              weekday, 
              totalLines: 0,
              uniqueDates: new Set() 
            };
          }
          
          // Each row IS a line
          groups[key].totalLines++;
          // Track unique dates to calculate average
          groups[key].uniqueDates.add(dateStr);
        });
        
        // Calculate averages - total lines divided by number of days
        const platformData = {};
        Object.values(groups).forEach(g => {
          if (!platformData[g.platform]) {
            platformData[g.platform] = {
              platform: g.platform,
              Sunday: 0, Monday: 0, Tuesday: 0, Wednesday: 0,
              Thursday: 0, Friday: 0, Saturday: 0
            };
          }
          
          // Average lines per day = total lines / number of unique days
          const avgLines = Math.round(g.totalLines / g.uniqueDates.size);
          platformData[g.platform][g.weekday] = avgLines;
        });
        
        console.log("Weekday heatmap sample:", platformData[Object.keys(platformData)[0]]);
        
        const chartData = Object.values(platformData)
          .sort((a, b) => a.platform.localeCompare(b.platform));
        
        console.log("Weekday heatmap loaded:", chartData.length, "platforms");
        setWeekdayHeatmapData(chartData);
        
      } catch (error) {
        console.error("Error loading weekday heatmap:", error);
      }
    };
    
    loadWeekdayHeatmap();
  }, []);

  // Summary statistics calculation from OBD Combined
  useEffect(() => {
    const calculateSummaryStats = async () => {
      try {
        console.log("=== CALCULATING SUMMARY STATS ===");
        
        const response = await fetch(getDataUrl('OBD Combined.csv'));
        const text = await response.text();
        const result = Papa.parse(text, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true
        });
        
        // Calculate totals
        const uniqueOBDs = new Set();
        let totalOrdered = 0;
        let totalPicked = 0;
        let totalLines = 0;
        
        result.data.forEach(row => {
          if (row['Delivery']) uniqueOBDs.add(row['Delivery']);
          totalOrdered += parseInt(row['Qty Order']) || 0;
          totalPicked += parseInt(row['Qty Pick']) || 0;
          totalLines++; // Each row is a line item
        });
        
        const fulfillmentRate = totalOrdered > 0 
          ? ((totalPicked / totalOrdered) * 100).toFixed(1)
          : 0;
        
        console.log("Summary stats calculated:");
        console.log("  Total Orders:", uniqueOBDs.size);
        console.log("  Total Lines:", totalLines.toLocaleString());
        console.log("  Total Qty Ordered:", totalOrdered.toLocaleString());
        console.log("  Total Qty Picked:", totalPicked.toLocaleString());
        console.log("  Fulfillment Rate:", fulfillmentRate + "%");
        
        setSummaryStats({
          totalOrders: uniqueOBDs.size,
          totalLines: totalLines,
          fulfillmentRate: parseFloat(fulfillmentRate),
          totalQtyOrdered: totalOrdered,
          totalQtyPicked: totalPicked
        });
        
      } catch (error) {
        console.error("Error calculating summary stats:", error);
      }
    };
    
    calculateSummaryStats();
  }, []);

  // Load cost data aligned with OBD date range
  useEffect(() => {
    const loadMonthlyCosts = async () => {
      try {
        console.log("=== LOADING COST DATA (YTD ALIGNED) ===");
        
        const response = await fetch(getDataUrl('Copy of 9678 FY25.xlsx'));
        const buffer = await response.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: 'array' });
        
        console.log("Available sheets:", workbook.SheetNames);
        
        // Read YTD costs from each month
        const getCost = (sheetName) => {
          if (!workbook.SheetNames.includes(sheetName)) return 0;
          const sheet = workbook.Sheets[sheetName];
          const cell = sheet['AD56'];
          return Math.abs(cell ? (cell.v || 0) : 0); // Convert to positive
        };
        
        const marchYTD = getCost('03.25');
        const aprilYTD = getCost('04.25');
        const septYTD = getCost('09.25');
        
        console.log("March YTD:", marchYTD.toLocaleString());
        console.log("April YTD:", aprilYTD.toLocaleString());
        console.log("September YTD:", septYTD.toLocaleString());
        
        // Calculate aligned cost:
        // 1. Cost from April 1 to Sept 30 = Sept YTD - March YTD
        const aprilToSept = septYTD - marchYTD;
        
        // 2. Full April cost = April YTD - March YTD
        const fullApril = aprilYTD - marchYTD;
        
        // 3. Half of April (we start April 14, so remove first half)
        const halfApril = fullApril / 2;
        
        // 4. Final aligned cost = April to Sept - (first half of April)
        const alignedCost = aprilToSept - halfApril;
        
        console.log("Full April cost:", fullApril.toLocaleString());
        console.log("Half April (to subtract):", halfApril.toLocaleString());
        console.log("April-Sept total:", aprilToSept.toLocaleString());
        console.log("ALIGNED COST (mid-April through Sept):", alignedCost.toLocaleString());
        
        setMonthlyCosts([{ totalCost: alignedCost }]);
        
      } catch (error) {
        console.error("Error loading cost data:", error);
      }
    };
    
    loadMonthlyCosts();
  }, []);

  // Load productivity metrics from OBD Sign-On data
  useEffect(() => {
    const loadProductivityMetrics = () => {
      try {
        console.log('=== LOADING PRODUCTIVITY METRICS ===');
        
        // Load all OBD data from localStorage
        const savedData = JSON.parse(localStorage.getItem('obdSignOnData') || '{}');
        
        // Aggregate data by associate across all dates
        const associateMetrics = {};
        
        Object.keys(savedData).forEach(date => {
          const obds = savedData[date] || [];
          
          obds.forEach(obd => {
            if (obd.status === 'completed' && obd.associateName) {
              if (!associateMetrics[obd.associateName]) {
                associateMetrics[obd.associateName] = {
                  associate: obd.associateName,
                  obdsCompleted: 0,
                  totalLines: 0,
                  totalUnits: 0,
                  totalHours: 0
                };
              }
              
              const assocData = associateMetrics[obd.associateName];
              assocData.obdsCompleted += 1;
              assocData.totalLines += obd.totalLines || 0;
              assocData.totalUnits += obd.totalUnits || 0;
              
              // Calculate hours from start and end time
              if (obd.startTime && obd.endTime) {
                const durationMs = new Date(obd.endTime) - new Date(obd.startTime);
                const durationHours = Math.max(0, durationMs / (1000 * 60 * 60));
                assocData.totalHours += durationHours;
              }
            }
          });
        });
        
        // Convert to array and calculate derived metrics
        const metrics = Object.values(associateMetrics).map(m => ({
          ...m,
          totalHours: Math.max(0.01, m.totalHours), // Minimum to avoid division by zero
          unitsPerHour: m.totalHours > 0 ? (m.totalUnits / m.totalHours).toFixed(1) : 0,
          linesPerHour: m.totalHours > 0 ? (m.totalLines / m.totalHours).toFixed(1) : 0,
          avgPickTime: m.totalLines > 0 ? ((m.totalHours * 60) / m.totalLines).toFixed(1) : 0
        }));
        
        console.log('Productivity metrics loaded:', metrics.length, 'associates');
        setProductivityMetrics(metrics);
        
      } catch (error) {
        console.error('Error loading productivity metrics:', error);
      }
    };
    
    loadProductivityMetrics();
    
    // Reload every 30 seconds to stay current with Sign-On updates
    const interval = setInterval(loadProductivityMetrics, 30000);
    return () => clearInterval(interval);
  }, []);

  // TEMPORARILY DISABLED - filter function
  // const filterByDateRange = (data) => {
  //   return filterMasterOBDDataByDateRange(data, dateRange);
  // };

  // Calculate overall statistics using filtered OBD data

  const calculateDispatchProgress = (allOBDs, completedOBDs, dispatchTimes) => {
    console.log('=== CALCULATING DISPATCH PROGRESS FROM MASTER DATA ===');
    
    // Use actual platform data from master OBD (no more synthetic mapping needed)
    const getObdPlatform = (obd) => {
      return obd.platform || 'Unknown';
    };

    // Group OBDs by dispatch time
    const dispatchGroups = {};
    
    allOBDs.forEach(obd => {
      const platform = getObdPlatform(obd);
      const dispatchTime = dispatchTimes[platform] || '5:00 PM';
      
      if (!dispatchGroups[dispatchTime]) {
        dispatchGroups[dispatchTime] = {
          time: dispatchTime,
          platforms: new Set(),
          totalOBDs: 0,
          completedOBDs: 0,
          totalLines: 0,
          completedLines: 0,
          totalUnits: 0,
          completedUnits: 0
        };
      }
      
      dispatchGroups[dispatchTime].platforms.add(platform);
      dispatchGroups[dispatchTime].totalOBDs++;
      dispatchGroups[dispatchTime].totalLines += obd.lines || obd.qtyOrdered || 0;
      dispatchGroups[dispatchTime].totalUnits += obd.units || obd.qtyOrdered || 0;
    });

    // Count completed OBDs, lines, and units for each dispatch time
    const completedObdNumbers = new Set(completedOBDs.map(obd => obd.obdNumber));
    allOBDs.forEach(obd => {
      if (completedObdNumbers.has(obd.obdNumber)) {
        const platform = getObdPlatform(obd);
        const dispatchTime = dispatchTimes[platform] || '5:00 PM';
        
        if (dispatchGroups[dispatchTime]) {
          dispatchGroups[dispatchTime].completedOBDs++;
          dispatchGroups[dispatchTime].completedLines += obd.lines || 1;
          dispatchGroups[dispatchTime].completedUnits += obd.units || obd.qtyOrdered || 0;
        }
      }
    });

    // Convert to array and sort by time
    const dispatchArray = Object.values(dispatchGroups).map(group => ({
      ...group,
      platforms: Array.from(group.platforms).sort(),
      percentage: group.totalOBDs > 0 ? (group.completedOBDs / group.totalOBDs) * 100 : 0
    })).sort((a, b) => {
      // Simple time sorting (would need better parsing for production)
      return a.time.localeCompare(b.time);
    });

    setDispatchProgress(dispatchArray);
    console.log('Dispatch Progress:', dispatchArray);
  };

  // REMOVED - old estimated overallStats (now using real summaryStats from OBD Combined)

  // TEMPORARILY DISABLED - using DataContext chart data instead
  // const filteredChartData = useMemo(() => {
  //   // Custom chart data from master OBD
  // }, [allOBDData, dateRange]);

  // TEMPORARILY DISABLED - using DataContext platformStats instead
  // const platformStats = useMemo(() => {
  //   // Custom calculation from master OBD data
  // }, [allOBDData]);

  // TEMPORARILY DISABLED - using DataContext data instead
  // All custom calculations commented out to restore basic functionality
  
  console.log("=== USING OBD COMBINED DATA EXCLUSIVELY ===");
  console.log("Loading:", loading);
  console.log("Platform fulfillment data loaded:", platformFulfillmentData.length, "platforms");

  // REMOVED - platformData calculation (was using undefined platformStats)
  // const platformData = useMemo(() => {
  //   console.log('=== CHART DATA TRANSFORMATION ===');
  //   // Old DataContext calculation removed
  // REMOVED - old platformData calculation fragments

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading Data...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="error">
          Error loading data: {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" gutterBottom>
              Warehouse Operations Dashboard
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  label="Date Range"
                  size="small"
                >
                  <MenuItem value="last14days">Last 14 Days</MenuItem>
                  <MenuItem value="lastMonth">Last Month</MenuItem>
                  <MenuItem value="allTime">All Time</MenuItem>
                </Select>
              </FormControl>
              
              {dateRange !== 'allTime' && (
                <Chip 
                  label={`Showing: ${dateRange === 'last14days' ? 'Last 14 Days' : 'Last Month'}`}
                  onDelete={() => setDateRange('allTime')}
                  color="primary"
                  size="small"
                />
              )}
            </Box>
          </Box>
        </Grid>

        {/* Summary Cards - All 4 in Top Row */}
        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ height: '100%', border: '1px solid #e0e0e0', boxShadow: 'none' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Total Orders Processed
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, mt: 1, color: '#1976d2' }}>
                {safeFormatNumber(summaryStats.totalOrders)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ height: '100%', border: '1px solid #e0e0e0', boxShadow: 'none' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Overall Fulfillment Rate
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, mt: 1, color: '#1976d2' }}>
                {safeFormatPercent(summaryStats.fulfillmentRate)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ height: '100%', border: '1px solid #e0e0e0', boxShadow: 'none' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Total Qty Ordered
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, mt: 1, color: '#1976d2' }}>
                {safeFormatNumber(summaryStats.totalQtyOrdered)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ height: '100%', border: '1px solid #e0e0e0', boxShadow: 'none' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Total Qty Picked
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, mt: 1, color: '#1976d2' }}>
                {safeFormatNumber(summaryStats.totalQtyPicked)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Detailed Productivity Metrics */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Detailed Productivity Metrics
            </Typography>
            {productivityMetrics.length > 0 ? (
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #ddd' }}>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Associate</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>OBDs Completed</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Total Lines</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Total Units</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Total Hours</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Units/Hour</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Lines/Hour</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Avg Pick Time (min/line)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productivityMetrics
                      .sort((a, b) => parseFloat(b.unitsPerHour) - parseFloat(a.unitsPerHour))
                      .map((metric, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px', fontWeight: 'bold' }}>{metric.associate}</td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>{metric.obdsCompleted}</td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>{metric.totalLines}</td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>{metric.totalUnits}</td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>{metric.totalHours.toFixed(2)}</td>
                        <td style={{ padding: '12px', textAlign: 'center', color: '#2196F3', fontWeight: 'bold' }}>
                          {metric.unitsPerHour}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center', color: '#4CAF50', fontWeight: 'bold' }}>
                          {metric.linesPerHour}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>{metric.avgPickTime}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            ) : (
              <Typography color="text.secondary">
                No productivity data available. Complete OBDs on the Sign-On page to see metrics.
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Data updates every 30 seconds from OBD Sign-On completions
            </Typography>
          </Paper>
        </Grid>

        {/* Daily OBD Progress Tracker */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Daily OBD Progress
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      OBDs Completed
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {obdProgress.completed} / {obdProgress.total}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={obdProgress.total > 0 ? (obdProgress.completed / obdProgress.total) * 100 : 0}
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                    {obdProgress.total > 0 ? ((obdProgress.completed / obdProgress.total) * 100).toFixed(1) : 0}% Complete
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      Lines Picked
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {obdProgress.completedLines} / {obdProgress.totalLines}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={obdProgress.totalLines > 0 ? (obdProgress.completedLines / obdProgress.totalLines) * 100 : 0}
                    sx={{ height: 10, borderRadius: 5 }}
                    color="success"
                  />
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                    {obdProgress.totalLines > 0 ? ((obdProgress.completedLines / obdProgress.totalLines) * 100).toFixed(1) : 0}% Complete
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      Units Picked
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {obdProgress.completedUnits} / {obdProgress.totalUnits}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={obdProgress.totalUnits > 0 ? (obdProgress.completedUnits / obdProgress.totalUnits) * 100 : 0}
                    sx={{ height: 10, borderRadius: 5 }}
                    color="secondary"
                  />
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                    {obdProgress.totalUnits > 0 ? ((obdProgress.completedUnits / obdProgress.totalUnits) * 100).toFixed(1) : 0}% Complete
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Dispatch Time Progress Bars */}
            {dispatchProgress.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  Progress by Dispatch Time
                </Typography>
                {dispatchProgress.map((dispatch, index) => (
                  <Box key={dispatch.time} sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                          {dispatch.time} Dispatch
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {dispatch.platforms.join(', ')}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {dispatch.completedOBDs} / {dispatch.totalOBDs} OBDs
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                          {dispatch.completedLines?.toLocaleString() || 0} / {dispatch.totalLines?.toLocaleString() || 0} Lines
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                          {dispatch.completedUnits?.toLocaleString() || 0} / {dispatch.totalUnits?.toLocaleString() || 0} Units
                        </Typography>
                      </Box>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={dispatch.percentage}
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        backgroundColor: '#f5f5f5',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: dispatch.percentage >= 80 ? '#4caf50' : 
                                         dispatch.percentage >= 60 ? '#ff9800' : '#f44336'
                        }
                      }}
                    />
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                      {dispatch.percentage.toFixed(1)}% Complete
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Cost Metrics Tiles */}
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Cost per Unit
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
              ${monthlyCosts.length > 0 && summaryStats.totalQtyPicked > 0
                ? (monthlyCosts[0].totalCost / summaryStats.totalQtyPicked).toFixed(2)
                : '0.00'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Apr 14 - Sept 30, 2025
            </Typography>
          </Paper>
        </Grid>
          
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Cost per OBD
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
              ${monthlyCosts.length > 0 && summaryStats.totalOrders > 0
                ? (monthlyCosts[0].totalCost / summaryStats.totalOrders).toFixed(2)
                : '0.00'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Apr 14 - Sept 30, 2025
            </Typography>
          </Paper>
        </Grid>
          
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Cost per Line
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
              ${monthlyCosts.length > 0 && summaryStats.totalLines > 0
                ? (monthlyCosts[0].totalCost / summaryStats.totalLines).toFixed(2)
                : '0.00'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Apr 14 - Sept 30, 2025 ({summaryStats.totalLines.toLocaleString()} total lines)
            </Typography>
          </Paper>
        </Grid>


        {/* NEW CHART 2 - Top 20 Stores from OBD Combined */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Top 20 Stores by Order Volume
            </Typography>
            {topStoresData.length > 0 ? (
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #ddd' }}>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Store</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Platform</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Name</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>OBDs</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Lines</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Units</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Fulfillment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topStoresData.map((store, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px' }}>{store.store}</td>
                        <td style={{ padding: '12px' }}>
                          <span style={{ 
                            backgroundColor: '#f0f0f0', 
                            padding: '4px 8px', 
                            borderRadius: '4px',
                            fontSize: '0.8em'
                          }}>
                            {store.platform}
                          </span>
                        </td>
                        <td style={{ padding: '12px' }}>{store.name}</td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>{store.obds.toLocaleString()}</td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>{store.lines.toLocaleString()}</td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>{store.units.toLocaleString()}</td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>{store.fulfillment}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            ) : (
              <Typography>Loading store data...</Typography>
            )}
          </Paper>
        </Grid>

        {/* NEW CHART 3 - Material Group Performance from OBD Combined */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Material Group Performance
            </Typography>
            {materialGroupData.length > 0 ? (
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #ddd' }}>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Material Group</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Qty Ordered</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Qty Picked</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Fill Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {materialGroupData.map((group, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px' }}>{group.group}</td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>{group.qtyOrdered.toLocaleString()}</td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>{group.qtyPicked.toLocaleString()}</td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>{group.fillRate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            ) : (
              <Typography>Loading material group data...</Typography>
            )}
          </Paper>
        </Grid>


        {/* Platform Fulfillment Rate Chart */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Platform Fulfillment Rate
            </Typography>
            {platformFulfillmentData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={platformFulfillmentData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="platform" />
                  <YAxis domain={[0, 100]} label={{ value: 'Fulfillment %', angle: -90, position: 'insideLeft' }} />
                  <Tooltip 
                    formatter={(value, name) => [`${value}%`, 'Fulfillment Rate']}
                    labelFormatter={(label) => `Platform: ${label}`}
                  />
                  <Bar dataKey="fulfillmentRate" fill="#4CAF50" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                <Typography color="text.secondary">Loading platform data...</Typography>
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Shows all 10+ platforms including BNA and ASH from OBD Combined data
            </Typography>
          </Paper>
        </Grid>

        {/* NEW CHART 6 - Weekday Heatmap by Platform */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Average Order Lines per Weekday by Platform
            </Typography>
            {weekdayHeatmapData.length > 0 ? (
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #ddd' }}>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Platform</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Sunday</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Monday</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Tuesday</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Wednesday</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Thursday</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Friday</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Saturday</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Calculate GLOBAL max across all platforms and days
                      const allValues = weekdayHeatmapData.flatMap(p => [
                        p.Sunday, p.Monday, p.Tuesday, p.Wednesday,
                        p.Thursday, p.Friday, p.Saturday
                      ]);
                      const globalMax = Math.max(...allValues);
                      
                      console.log("Heatmap global max:", globalMax);
                      
                      const getCellStyle = (value) => {
                        const intensity = value / globalMax; // Use global max, not per-platform
                        return {
                          padding: '12px',
                          textAlign: 'center',
                          backgroundColor: `rgba(33, 150, 243, ${intensity})`,
                          color: intensity > 0.6 ? 'white' : 'black',
                          fontWeight: intensity > 0.5 ? 'bold' : 'normal'
                        };
                      };
                      
                      return weekdayHeatmapData.map((platform, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '12px', fontWeight: 'bold' }}>{platform.platform}</td>
                          <td style={getCellStyle(platform.Sunday)}>{platform.Sunday}</td>
                          <td style={getCellStyle(platform.Monday)}>{platform.Monday}</td>
                          <td style={getCellStyle(platform.Tuesday)}>{platform.Tuesday}</td>
                          <td style={getCellStyle(platform.Wednesday)}>{platform.Wednesday}</td>
                          <td style={getCellStyle(platform.Thursday)}>{platform.Thursday}</td>
                          <td style={getCellStyle(platform.Friday)}>{platform.Friday}</td>
                          <td style={getCellStyle(platform.Saturday)}>{platform.Saturday}</td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Color intensity indicates average number of lines
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 2 }}>
                    <Box sx={{ width: 20, height: 20, bgcolor: 'rgba(33, 150, 243, 0.2)' }} />
                    <Typography variant="caption">Low</Typography>
                    <Box sx={{ width: 20, height: 20, bgcolor: 'rgba(33, 150, 243, 0.5)', ml: 1 }} />
                    <Box sx={{ width: 20, height: 20, bgcolor: 'rgba(33, 150, 243, 0.8)' }} />
                    <Box sx={{ width: 20, height: 20, bgcolor: 'rgba(33, 150, 243, 1)' }} />
                    <Typography variant="caption">High</Typography>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Typography>Loading heatmap data...</Typography>
            )}
          </Paper>
        </Grid>



        {/* OLD CHART REMOVED - Material Groups using DataContext */}

        {/* OLD CHART REMOVED - Daily Quantity Picked using DataContext */}

        {/* OLD CHART REMOVED - Weekly Quantity by Store using DataContext */}

        {/* OLD CHART REMOVED - Daily Picked Quantity by Platform using DataContext */}

        {/* OLD CHARTS REMOVED - Fill Rate and Heatmap using DataContext */}


        {/* OLD CHART REMOVED - Platform Performance Comparison */}

        {/* DUPLICATE CHART REMOVED - Weekly Volume using old data */}

        {/* NEW CHART 1 - Daily Picked Units (LARGE VERSION) */}
        <Grid item xs={12} sx={{ width: '100vw', maxWidth: '100vw', ml: 'calc(-50vw + 50%)' }}>
          <Box sx={{ width: '100%', px: 2 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Daily Picked Units (Enhanced View)
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Total units picked per day from OBD Combined data
              </Typography>
              {dailyUnitsData.length > 0 ? (
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <ResponsiveContainer width="100%" height={700} minWidth={1200}>
                    <LineChart 
                      data={dailyUnitsData}
                      margin={{ top: 20, right: 50, left: 50, bottom: 100 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={100}
                        interval="preserveStartEnd"
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        label={{ value: 'Units Picked', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip 
                        formatter={(value) => value.toLocaleString()} 
                        labelStyle={{ fontWeight: 'bold' }}
                      />
                      <Legend wrapperStyle={{ paddingTop: '20px' }} />
                      <Line 
                        type="monotone" 
                        dataKey="qtyPicked" 
                        stroke="#2196F3" 
                        strokeWidth={3}
                        name="Units Picked"
                        dot={{ r: 3, fill: '#2196F3' }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Typography>Loading data...</Typography>
              )}
            </Paper>
          </Box>
        </Grid>

        {/* NEW CHART 2 - Weekly Volume & Fill Rate Analysis (LARGE VERSION) */}
        <Grid item xs={12} sx={{ width: '100vw', maxWidth: '100vw', ml: 'calc(-50vw + 50%)' }}>
          <Box sx={{ width: '100%', px: 2 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Weekly Volume & Fill Rate Analysis (Enhanced View)
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Weekly order volume with fill rate trends and linear regression
              </Typography>
              {weeklyTrendsData.length > 0 ? (
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <ResponsiveContainer width="100%" height={700} minWidth={1200}>
                    <ComposedChart 
                      data={weeklyTrendsData}
                      margin={{ top: 20, right: 80, left: 50, bottom: 100 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis 
                        dataKey="week" 
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={100}
                      />
                      <YAxis 
                        yAxisId="left"
                        tick={{ fontSize: 12 }}
                        label={{ value: 'Order Volume', angle: -90, position: 'insideLeft' }}
                      />
                      <YAxis 
                        yAxisId="right" 
                        orientation="right"
                        domain={[0, 100]}
                        tick={{ fontSize: 12 }}
                        label={{ value: 'Fill Rate (%)', angle: 90, position: 'insideRight' }}
                      />
                      <Tooltip />
                      <Legend wrapperStyle={{ paddingTop: '20px' }} />
                      <Bar 
                        yAxisId="left" 
                        dataKey="volume" 
                        fill="#2196F3" 
                        name="Order Volume"
                      />
                      <Line 
                        yAxisId="right" 
                        type="monotone" 
                        dataKey="fillRate" 
                        stroke="#4CAF50" 
                        strokeWidth={3}
                        name="Fill Rate (%)"
                        dot={{ r: 4 }}
                      />
                      <Line 
                        yAxisId="right" 
                        type="monotone" 
                        dataKey="fillRateTrend" 
                        stroke="#FF9800" 
                        strokeWidth={3}
                        strokeDasharray="8 8"
                        name="Fill Rate Trend"
                        dot={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Typography>Loading data...</Typography>
              )}
            </Paper>
          </Box>
        </Grid>

      </Grid>
    </Container>
  );
};

export default WarehouseView;